/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import React from 'react';
import { toast } from 'react-toastify';

import {
  Button,
  Dimmer,
  Form,
  Header,
  Loader,
  Modal,
} from '@mairistem/composants';
import { request } from '@mairistem/tools';

import { useFetchHubeeStatus } from '@/hooks';
import { getHubeeParametrage, getHubeePing } from '@/routes';
import type { ParametrageHubee } from '@/types';

// Comme dans RCO : sur le domaine de production, les accès HubEE de
// production sont imposés et non modifiables.
const HUBEE_PROD_HOSTNAME = 'pacs.jvsonline.fr';
const HUBEE_PROD_API_URL = 'https://api.hubee.numerique.gouv.fr';
const HUBEE_PROD_API_VERSION = 'v1';
const HUBEE_PROD_TOKEN_URL =
  'https://auth.hubee.numerique.gouv.fr/oauth2/token';

const isHubeeProdHost = () =>
  typeof window !== 'undefined' &&
  window.location.hostname === HUBEE_PROD_HOSTNAME;

const PARAMETRAGE_VIDE: ParametrageHubee = {
  liaisonActive: false,
  apiUrl: '',
  apiVersion: '',
  apiToken: '',
  apiLogin: '',
  apiMdp: '',
};

interface ModalParametresHubeeProps {
  onClose: () => void;
}

/**
 * Paramétrage de la liaison HubEE de l'entité (démarche « dépôt de dossier
 * PACS »), sur le modèle de l'onglet HUBEE des paramètres généraux de RCO.
 */
const ModalParametresHubee = ({ onClose }: ModalParametresHubeeProps) => {
  const { fetchHubeeStatus } = useFetchHubeeStatus();

  const [parametrage, setParametrage] =
    React.useState<ParametrageHubee>(PARAMETRAGE_VIDE);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [erreurs, setErreurs] = React.useState<Record<string, string>>({});

  React.useEffect(() => {
    request
      .get(getHubeeParametrage())
      .then((response: any) => {
        setParametrage({ ...PARAMETRAGE_VIDE, ...response });
      })
      .catch(() => {
        toast.error('Erreur lors de la récupération du paramétrage HubEE');
      })
      .finally(() => setLoading(false));
  }, []);

  const handleChange = React.useCallback(
    (_e: React.SyntheticEvent, { name, value, checked }: any) => {
      setParametrage((prev) => ({ ...prev, [name]: checked ?? value }));
      setErreurs((prev) => ({ ...prev, [name]: '' }));
    },
    [],
  );

  const hoteProduction = isHubeeProdHost();

  // Accès affichés, et enregistrés : ceux de production sur le domaine de
  // production, ceux du paramétrage ailleurs.
  const acces = React.useMemo(
    () =>
      hoteProduction
        ? {
            apiUrl: HUBEE_PROD_API_URL,
            apiVersion: HUBEE_PROD_API_VERSION,
            apiToken: HUBEE_PROD_TOKEN_URL,
          }
        : {
            apiUrl: parametrage.apiUrl,
            apiVersion: parametrage.apiVersion,
            apiToken: parametrage.apiToken,
          },
    [
      hoteProduction,
      parametrage.apiUrl,
      parametrage.apiVersion,
      parametrage.apiToken,
    ],
  );

  const handleSave = React.useCallback(() => {
    setSaving(true);
    setErreurs({});

    const { apiMdp, liaisonActive, apiLogin } = parametrage;
    const { apiUrl, apiVersion, apiToken } = acces;

    request
      .put(getHubeeParametrage(), {
        liaisonActive: !!liaisonActive,
        apiUrl: apiUrl || null,
        apiVersion: apiVersion || null,
        apiToken: apiToken || null,
        apiLogin: apiLogin || null,
        apiMdp: apiMdp || null,
      })
      .then(async () => {
        toast.success('Paramétrage HubEE enregistré');

        // Comme dans RCO : on vérifie tout de suite les identifiants, puis
        // on rafraîchit l'état de la liaison utilisé par la liste.
        if (liaisonActive) {
          const ping = (await request.get(getHubeePing())) as {
            error?: string | null;
          };

          if (ping?.error) {
            toast.error(ping.error, { autoClose: false });
          } else {
            toast.success('Connexion à HubEE réussie');
          }
        }

        await fetchHubeeStatus();

        onClose();
      })
      .catch((error: any) => {
        // Erreurs de validation de l'API (422) : affichées sous les champs
        const champs =
          error?.response?.data?.errors ?? error?.data?.errors ?? null;

        if (champs) {
          setErreurs(
            Object.fromEntries(
              Object.entries(champs).map(([champ, messages]) => [
                champ,
                String((messages as string[])[0] ?? ''),
              ]),
            ),
          );
        }

        toast.error("Erreur lors de l'enregistrement du paramétrage HubEE");
      })
      .finally(() => setSaving(false));
  }, [parametrage, acces, onClose, fetchHubeeStatus]);

  const { liaisonActive, apiLogin, apiMdp } = parametrage;
  const { apiUrl, apiVersion, apiToken } = acces;
  const champsDesactives = !liaisonActive || saving;
  const accesDesactives = hoteProduction || champsDesactives;

  const panes = [
    {
      menuItem: 'HUBEE',
      render: () => (
        <Tab.Pane>
          <Header>INFORMATIONS DE CONNEXION</Header>
          <Form style={{ padding: '0 2em' }}>
            <Form.Group
              style={{ flexDirection: 'column', alignItems: 'flex-end' }}
            >
              <Form.Checkbox
                label="Liaison active"
                data-testid="parametres-hubee-liaison-active"
                name="liaisonActive"
                checked={!!liaisonActive}
                disabled={saving}
                onChange={handleChange}
              />
            </Form.Group>
            <Form.Group>
              <Form.Input
                width={11}
                disabled={accesDesactives}
                name="apiUrl"
                input={{ 'data-testid': 'parametres-hubee-api-url' }}
                label="Accès API (URL)"
                value={apiUrl ?? ''}
                error={erreurs.apiUrl || undefined}
                onChange={handleChange}
              />
              <Form.Input
                width={5}
                disabled={accesDesactives}
                name="apiVersion"
                input={{ 'data-testid': 'parametres-hubee-api-version' }}
                label="Version API"
                value={apiVersion ?? ''}
                error={erreurs.apiVersion || undefined}
                onChange={handleChange}
              />
            </Form.Group>
            <Form.Input
              width={16}
              disabled={accesDesactives}
              name="apiToken"
              input={{ 'data-testid': 'parametres-hubee-api-token' }}
              label="Accès TOKEN (URL)"
              value={apiToken ?? ''}
              error={erreurs.apiToken || undefined}
              onChange={handleChange}
            />
            <Form.Group widths="equal">
              <Form.Input
                disabled={champsDesactives}
                name="apiLogin"
                input={{ 'data-testid': 'parametres-hubee-api-login' }}
                label="Utilisateur"
                value={apiLogin ?? ''}
                error={erreurs.apiLogin || undefined}
                onChange={handleChange}
              />
              <Form.Input
                disabled={champsDesactives}
                name="apiMdp"
                input={{ 'data-testid': 'parametres-hubee-api-mdp' }}
                label="Mot de passe"
                value={apiMdp ?? ''}
                error={erreurs.apiMdp || undefined}
                onChange={handleChange}
              />
            </Form.Group>
          </Form>
        </Tab.Pane>
      ),
    },
  ];
  const formulaire = (
    <>
      <Header>INFORMATIONS DE CONNEXION</Header>
      <Form style={{ padding: '0 2em' }}>
        <Form.Group style={{ flexDirection: 'column', alignItems: 'flex-end' }}>
          <Form.Checkbox
            label="Liaison active"
            data-testid="parametres-hubee-liaison-active"
            name="liaisonActive"
            checked={!!liaisonActive}
            disabled={saving}
            onChange={handleChange}
          />
        </Form.Group>
        <Form.Group>
          <Form.Input
            width={11}
            disabled={accesDesactives}
            name="apiUrl"
            data-testid="parametres-hubee-api-url"
            label="Accès API (URL)"
            value={apiUrl ?? ''}
            error={erreurs.apiUrl || undefined}
            onChange={handleChange}
          />
          <Form.Input
            width={5}
            disabled={accesDesactives}
            name="apiVersion"
            data-testid="parametres-hubee-api-version"
            label="Version API"
            value={apiVersion ?? ''}
            error={erreurs.apiVersion || undefined}
            onChange={handleChange}
          />
        </Form.Group>
        <Form.Input
          width={16}
          disabled={accesDesactives}
          name="apiToken"
          data-testid="parametres-hubee-api-token"
          label="Accès TOKEN (URL)"
          value={apiToken ?? ''}
          error={erreurs.apiToken || undefined}
          onChange={handleChange}
        />
        <Form.Group widths="equal">
          <Form.Input
            disabled={champsDesactives}
            name="apiLogin"
            data-testid="parametres-hubee-api-login"
            label="Utilisateur"
            value={apiLogin ?? ''}
            error={erreurs.apiLogin || undefined}
            onChange={handleChange}
          />
          <Form.Input
            disabled={champsDesactives}
            name="apiMdp"
            input={{ 'data-testid': 'parametres-hubee-api-mdp' }}
            label="Mot de passe"
            value={apiMdp ?? ''}
            error={erreurs.apiMdp || undefined}
            onChange={handleChange}
          />
        </Form.Group>
      </Form>
    </>
  );

  return (
    <Modal open size="small" data-testid="parametres-hubee-modal">
      <Modal.Header>Paramètres HubEE</Modal.Header>
      <Modal.Content style={{ position: 'relative', minHeight: '10em' }}>
        {loading ? (
          <Dimmer active inverted>
            <Loader inverted>Chargement des paramètres</Loader>
          </Dimmer>
        ) : (
          formulaire
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button
          basic
          size="tiny"
          icon="cancel"
          content="Annuler"
          data-testid="parametres-hubee-annuler"
          disabled={saving}
          onClick={onClose}
        />
        <Button
          positive
          icon="check"
          content="Enregistrer"
          data-testid="parametres-hubee-enregistrer"
          loading={saving}
          disabled={loading || saving}
          onClick={handleSave}
        />
      </Modal.Actions>
    </Modal>
  );
};

export default ModalParametresHubee;
